import { memo, useState, useEffect, useCallback } from 'react'
import { TagIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { vendorAPI } from '../../services/api/vendor'
import { createLogger } from '../../utils/logger'

const logger = createLogger('EntityOfficialMenu')

/**
 * EntityOfficialMenu - Displays official vendor-entered menu items for visitors.
 * Fetches from the public /entities/:id/official-menu endpoint.
 * Renders nothing if the entity has no official items.
 * @idempotent @atomic
 */
const EntityOfficialMenu = memo(function EntityOfficialMenu({ entityId, t }) {
  const [items, setItems] = useState([])
  const [open, setOpen] = useState(true)
  const { execute } = useAsyncOperation()

  const fetchMenu = useCallback(() => {
    if (!entityId) return
    execute(() => vendorAPI.getOfficialOfferings(entityId), {
      silent: true,
      onSuccess: (data) => {
        const arr = Array.isArray(data) ? data : (data?.data || [])
        setItems(arr)
      },
      onError: (err) => logger.debug('No official menu available', { entityId, err })
    }).catch(() => {/* optional feature — silently ignore if endpoint returns 404 */})
  }, [entityId, execute])

  useEffect(() => { fetchMenu() }, [fetchMenu])

  if (items.length === 0) return null

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 overflow-hidden mb-2">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-900 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <TagIcon className="w-4 h-4 text-primary" />
          {t('vendor.officialMenu', 'Official Menu')}
          <span className="text-gray-400 font-normal">({items.length})</span>
        </span>
        {open ? <ChevronUpIcon className="w-4 h-4 text-gray-500" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500" />}
      </button>
      {open && (
        <div className="border-t border-primary/10 divide-y divide-primary/10">
          {items.map(item => (
            <div key={item.id} className="flex items-start justify-between px-3 py-2 gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-800">{item.name}</p>
                {item.description && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.description}</p>
                )}
              </div>
              {item.price != null && (
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                  ${Number(item.price).toFixed(2)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

export default EntityOfficialMenu
